/**
 * QbPushReviewDialog — pre-flight "Review & Push to QuickBooks" dialog.
 *
 * Lets admins choose which optional sections (job description, completion
 * notes, job notes, work order #) get pushed to the QBO invoice's
 * PrivateNote, with a live preview of the exact text QBO will receive.
 *
 * PO Number is ALWAYS pushed when set on the job — it goes into QBO's
 * native Invoice.PONumber field, visible on the customer PDF.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   job: { id, ... }
 *   onPushed: (result) => void   — fired after successful QBO push
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Loader2, FileText, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const SECTION_LABELS = {
  include_work_order_number: 'Work Order #',
  include_description: 'Job Description / Customer Requests (from job creation)',
  include_completion_notes: 'Completion Notes',
  include_job_notes: 'Job Notes (most-recent first, up to 10)',
};

export default function QbPushReviewDialog({ open, onOpenChange, job, qbConnected = true, onPushed }) {
  const { toast } = useToast();
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [options, setOptions] = useState({
    include_work_order_number: true,
    include_description: true,
    include_completion_notes: true,
    include_job_notes: true,
  });

  const fetchPreview = useCallback(async (opts) => {
    if (!job?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/qb/sync-job/${job.id}/preview`,
        { push_options: opts },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreview(res.data);
      // Seed options from server-saved values on first load (when we haven't
      // tweaked anything yet — preserve the admin's last-picked profile).
      if (res.data?.saved_options && Object.keys(res.data.saved_options).length > 0) {
        setOptions((prev) => ({ ...prev, ...res.data.saved_options }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not load preview');
    } finally {
      setLoading(false);
    }
  }, [job?.id, token]);

  // Initial load when dialog opens
  useEffect(() => {
    if (open && job?.id) {
      fetchPreview(options);
    }
    if (!open) {
      setPreview(null);
      setError(null);
    }
  }, [open, job?.id]);  // intentionally do not depend on options/fetchPreview — onToggle re-fetches

  // Re-fetch preview on every option toggle (cheap — no QBO call)
  const onToggle = (key) => {
    const next = { ...options, [key]: !options[key] };
    setOptions(next);
    fetchPreview(next);
  };

  const handlePush = async () => {
    if (!job?.id) return;
    setPushing(true);
    setError(null);
    const isRepush = !!preview?.already_synced;
    try {
      const res = await axios.post(
        `${API}/qb/sync-job/${job.id}`,
        { push_options: options, persist_options: true, force: isRepush },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data?.updated_fields || [];
      const noteMsg = res.data?.note;
      toast({
        title: isRepush ? 'QuickBooks invoice updated' : 'Pushed to QuickBooks',
        description: isRepush
          ? (noteMsg
              || (updated.length
                  ? `Updated: ${updated.join(', ')} on Invoice ${res.data?.invoice_id || ''}.`
                  : 'Invoice header refreshed.'))
          : (res.data?.invoice_id
              ? `Invoice ${res.data.invoice_id} created.`
              : 'Invoice created in QuickBooks.'),
      });
      onPushed?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Push failed';
      setError(detail);
      toast({
        title: isRepush ? 'QuickBooks re-push failed' : 'QuickBooks push failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setPushing(false);
    }
  };

  const sections = preview?.sections || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="qb-push-review-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Review & Push to QuickBooks
          </DialogTitle>
          <DialogDescription>
            Choose which job details to include on the QuickBooks invoice. Your
            selections are remembered per-job so re-pushes stay fast.
          </DialogDescription>
        </DialogHeader>

        {loading && !preview && (
          <div className="flex items-center justify-center py-8 text-gray-500" data-testid="qb-review-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading preview…
          </div>
        )}

        {preview?.already_synced && (
          <div
            className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-900"
            data-testid="qb-review-already-synced"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              This job is already invoiced in QuickBooks (Invoice ID{' '}
              <span className="font-mono">{job?.qb_invoice_id}</span>). The Push button below
              will <strong>update the existing invoice&apos;s header</strong> — PO Number,
              PrivateNote, and Note to Customer — without changing the invoice number,
              line items, or totals.
            </div>
          </div>
        )}

        {!qbConnected && !preview?.already_synced && (
          <div
            className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-300 rounded text-sm text-rose-900"
            data-testid="qb-review-not-connected"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              QuickBooks isn&apos;t connected. You can review and save your push selections,
              but the Push button is disabled until you connect under{' '}
              <strong>Settings → QuickBooks Online</strong>.
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-5">
            {/* PO Number always-on row */}
            <div className="space-y-2" data-testid="qb-review-po-row">
              <Label className="text-sm font-semibold">PO Number (always pushed when set)</Label>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border rounded">
                {preview.po_number ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="font-mono text-sm" data-testid="qb-review-po-value">
                      {preview.po_number}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">→ QBO Invoice PO Number (visible on customer PDF)</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500" data-testid="qb-review-po-empty">
                      No PO Number on this job — add it via Edit Job if your customer provided one.
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Section toggles */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Include in QuickBooks PrivateNote</Label>
              <div className="space-y-2">
                {sections.map((s) => {
                  const checked = !!options[s.key];
                  const disabled = !s.available;
                  return (
                    <div
                      key={s.key}
                      className={`flex items-start gap-3 p-3 border rounded ${disabled ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                      data-testid={`qb-review-section-${s.key}`}
                    >
                      <Checkbox
                        id={`qb-opt-${s.key}`}
                        checked={!disabled && checked}
                        onCheckedChange={() => !disabled && onToggle(s.key)}
                        disabled={disabled}
                        data-testid={`qb-review-toggle-${s.key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <label
                          htmlFor={`qb-opt-${s.key}`}
                          className={`text-sm font-medium block ${disabled ? '' : 'cursor-pointer'}`}
                        >
                          {SECTION_LABELS[s.key] || s.label}
                          {disabled && (
                            <span className="ml-2 text-xs text-gray-500 font-normal">
                              (none set on this job)
                            </span>
                          )}
                        </label>
                        {s.available && s.content && (
                          <div className="mt-1 text-xs text-gray-600 whitespace-pre-wrap line-clamp-4 break-words">
                            {s.content}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Live preview */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">QuickBooks PrivateNote (hidden from customer)</Label>
              <pre
                className="text-xs bg-gray-900 text-gray-100 p-3 rounded max-h-64 overflow-y-auto whitespace-pre-wrap break-words"
                data-testid="qb-review-private-note"
              >
                {preview.private_note || '(empty)'}
              </pre>
            </div>

            {preview.customer_memo && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Note to Customer (visible on QBO PDF)</Label>
                <pre
                  className="text-xs bg-emerald-50 text-emerald-900 p-3 rounded max-h-32 overflow-y-auto whitespace-pre-wrap break-words border border-emerald-200"
                  data-testid="qb-review-customer-memo"
                >
                  {preview.customer_memo}
                </pre>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-300 rounded text-sm text-rose-900" data-testid="qb-review-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pushing}
            data-testid="qb-review-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handlePush}
            disabled={pushing || loading || !preview || !qbConnected}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="qb-review-push-btn"
          >
            {pushing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {preview?.already_synced ? 'Updating…' : 'Pushing…'}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {preview?.already_synced ? 'Re-push to QuickBooks' : 'Push to QuickBooks'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
